<script setup lang="ts">
import { captureException } from '@sentry/vue';
import { sendPasswordResetEmail } from 'firebase/auth';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { auth } from '../firebase';
import AuthLayout from '../layouts/AuthLayout.vue';

definePage({
  meta: {
    isPublic: true,
  },
});

const router = useRouter();
const toast = useToast();

const email = ref<string>('');
const loading = ref(false);
const emailSent = ref(false);

async function resetPassword() {
  if (!email.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please enter your email address',
      life: 3000,
    });
    return;
  }

  try {
    loading.value = true;
    await sendPasswordResetEmail(auth, email.value);
    emailSent.value = true;
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Password reset email sent successfully',
      life: 3000,
    });
  }
  catch (err: any) {
    let errorMessage = 'An error occurred while sending reset email';
    captureException(err);

    if (err.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email address';
    }
    else if (err.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    }
    else if (err.code === 'auth/too-many-requests') {
      errorMessage = 'Too many requests. Please try again later';
    }

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000,
    });
  }
  finally {
    loading.value = false;
  }
}

function goToLogin() {
  router.push('/login');
}
</script>

<template>
  <AuthLayout>
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold text-900 mb-3">
        Reset Password
      </h1>
      <p class="text-600 text-lg">
        Enter your email address and we'll send you a link to reset your password
      </p>
    </div>

    <div v-if="emailSent" class="text-center">
      <div class="mb-6">
        <i class="pi pi-check-circle text-6xl text-green-500 mb-4" />
        <h2 class="text-xl font-semibold text-900 mb-2">
          Email Sent!
        </h2>
        <p class="text-600">
          We've sent a password reset link to <strong>{{ email }}</strong>
        </p>
        <p class="text-600 mt-2">
          Check your email and follow the instructions to reset your password.
        </p>
      </div>
      <Button
        label="Back to Login"
        class="w-full"
        @click="goToLogin"
      />
    </div>

    <form v-else class="flex flex-col gap-6" @submit.prevent="resetPassword">
      <div class="flex flex-col gap-2">
        <label for="email" class="text-900 font-medium">Email Address</label>
        <InputText
          id="email"
          v-model="email"
          type="email"
          placeholder="Enter your email address"
          class="w-full"
          :disabled="loading"
          autofocus
        />
      </div>

      <div class="flex flex-col gap-3">
        <Button
          type="submit"
          label="Send Reset Email"
          class="w-full"
          :loading="loading"
        />
        <Button
          label="Back to Login"
          class="w-full"
          severity="secondary"
          outlined
          @click="goToLogin"
        />
      </div>
    </form>
  </AuthLayout>
</template>
